// Variant selector — resolves the selected variant from option radios, updates
// the hidden `id` input, refreshes price + buy buttons via the Section
// Rendering API, syncs the URL, and notifies the media gallery.
if (!customElements.get("variant-selector")) {
  customElements.define(
    "variant-selector",
    class VariantSelector extends HTMLElement {
      constructor() {
        super();
        this.onChange = this.onChange.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.productUrl = this.dataset.url;
        this.idInput = this.querySelector("[data-variant-id]");
        this.section = this.closest("[data-product-section]");
        try {
          const json = this.querySelector("[data-variant-json]")?.textContent;
          this.variants = json ? JSON.parse(json) : [];
        } catch (_e) {
          this.variants = [];
        }
        // Enhance: enable the JS-driven id input so it (not the noscript select)
        // is what submits.
        if (this.idInput) this.idInput.disabled = false;
        this.addEventListener("change", this.onChange);
      }

      disconnectedCallback() {
        this.removeEventListener("change", this.onChange);
      }

      get selectedOptions() {
        return Array.from(this.querySelectorAll("input[type='radio']:checked"))
          .sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition)
          .map((input) => input.value);
      }

      getMatchingVariant() {
        const selected = this.selectedOptions;
        if (!selected.length) return this.variants[0];
        return this.variants.find((variant) =>
          variant.options.every((opt, i) => opt === selected[i]),
        );
      }

      onChange() {
        const variant = this.getMatchingVariant();
        this.updateVariant(variant);
      }

      updateVariant(variant) {
        if (this.idInput) {
          this.idInput.value = variant ? variant.id : "";
          this.idInput.disabled = !variant;
        }

        // Tell the gallery to show this variant's image.
        if (variant?.featured_media) {
          (this.section || document).dispatchEvent(
            new CustomEvent("variant:change", {
              bubbles: true,
              detail: { variant, featuredMediaId: variant.featured_media.id },
            }),
          );
        }

        this.updateUrl(variant);
        this.renderSection(variant);
      }

      updateUrl(variant) {
        if (!variant || !this.productUrl) return;
        const url = new URL(this.productUrl, window.location.origin);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      }

      async renderSection(variant) {
        if (!this.sectionId) return;
        const params = new URLSearchParams({ section_id: this.sectionId });
        if (variant) params.set("variant", variant.id);
        const url = `${this.productUrl}?${params.toString()}`;
        try {
          const res = await fetch(url);
          if (!res.ok) return;
          const text = await res.text();
          const doc = new DOMParser().parseFromString(text, "text/html");
          this.swap(doc, "[data-price]");
          this.swap(doc, "[data-buy-buttons]");
        } catch (_e) {
          // Network failure: leave current DOM; the form still posts the right id.
        }
      }

      swap(doc, selector) {
        const root = this.section || document;
        const next = doc.querySelector(selector);
        const current = root.querySelector(selector);
        if (next && current) current.innerHTML = next.innerHTML;
      }
    },
  );
}
